import { TipoCita, ProgramacionServicio, AgendaDisponibilidad, ProgramacionServicioRequest, CancelarCitaSPRequest } from '../../model/agenda/agenda';
import { ConfiguracionCita } from '../../model/agenda/configuracion-cita';
import { Profesional, AgendaProfesionalPageResponse } from '../../model/profesional/profesional';

/**
 * Puerto Abstracto: AgendaRepository
 * 
 * Define el contrato para gestión de agenda (programación de citas).
 * Migrado desde: AdminScheduleActivity, CrearCitaAdministrativaActivity
 * 
 * Arquitectura Hexagonal:
 * - Este puerto está en el dominio
 * - HttpAgendaRepository (adaptador) lo implementa en infraestructura
 * 
 * @author Emerson Rodriguez - Ingeneo
 */
export abstract class AgendaRepository {

  // === Calendario/Disponibilidad ===

  /**
   * Consulta la disponibilidad de un asesor en un mes específico
   */
  abstract consultarCalendarioMes(
    asesorId: number,
    anio: number,
    mes: number
  ): Promise<AgendaDisponibilidad[]>;

  /**
   * Consulta las citas de un asesor en una fecha específica
   */
  abstract consultarCitasDia(
    asesorId: number,
    fecha: string
  ): Promise<ProgramacionServicio[]>;

  // === CRUD Citas ===

  /**
   * Obtiene una cita por ID
   */
  abstract obtenerCita(citaId: number): Promise<ProgramacionServicio>;

  /**
   * Crea una nueva cita
   */
  abstract crearCita(cita: ProgramacionServicioRequest): Promise<ProgramacionServicio>;

  /**
   * Actualiza una cita existente
   */
  abstract actualizarCita(citaId: number, cita: ProgramacionServicioRequest): Promise<ProgramacionServicio>;

  /**
   * Cancela una cita
   */
  abstract cancelarCita(citaId: number, motivo?: string): Promise<ProgramacionServicio>;

  /**
   * Cancela una cita usando el SP del legacy con todos los parámetros.
   * 
   * Incluye:
   * - Tipo de cancelador (ASE, PRO, BEN, BES, INT)
   * - Nombre del contacto (condicional)
   * - Justificación (condicional para BES)
   * - Observaciones
   */
  abstract cancelarCitaSP(request: CancelarCitaSPRequest): Promise<void>;

  // === Tipos de Cita ===

  /**
   * Lista los tipos de cita activos para citas administrativas.
   * 
   * Sigue el legacy (CrearCitaAdministrativaViewImpl.java línea 2056-2058):
   * Los tipos se filtran por TPSC_TIPO_CITA_EMPLEADO donde NMCONSEC_EMPLEADO corresponde al empleado.
   * 
   * @param dniEmpleado DNI del empleado (ej: "C43480070") - se usa para filtrar tipos de cita por asesor
   * @param usoCita 'ADMIN' para administrativas, 'SERVICIO' para Campo/Informe (default: 'ADMIN')
   */
  abstract listarTiposCita(dniEmpleado?: string, usoCita?: 'ADMIN' | 'SERVICIO'): Promise<TipoCita[]>;

  /**
   * Obtiene un tipo de cita por ID
   */
  abstract obtenerTipoCita(tipoCitaId: number): Promise<TipoCita>;

  // === Búsqueda ===

  /**
   * Consulta las citas de un asesor en un rango de fechas
   */
  abstract consultarCitasRango(
    asesorId: number,
    fechaInicio: string,
    fechaFin: string
  ): Promise<ProgramacionServicio[]>;

  /**
   * Consulta citas por CDPERSONA del asesor (de TPSC_PERSONAS).
   * Usa el endpoint que hace JOIN con TPSC_EMPLEADOS para resolver
   * el nmconsecutivo_asesor correcto.
   */
  abstract consultarCitasRangoByCdpersona(
    cdpersona: number,
    fechaInicio: string,
    fechaFin: string
  ): Promise<ProgramacionServicio[]>;

  /**
   * Consulta citas por DNI del asesor (de TPSC_PERSONAS).
   * Sigue el patrón del legacy (SP_DIS_CONSULTAR_CITAS_AGENDA con ivadniprestador).
   * Este es el método recomendado ya que replica el comportamiento exacto del sistema legacy.
   */
  abstract consultarCitasRangoByDni(
    dni: string,
    fechaInicio: string,
    fechaFin: string
  ): Promise<ProgramacionServicio[]>;

  /**
   * Consulta disponibilidad por CDPERSONA del asesor
   */
  abstract consultarDisponibilidadByCdpersona(
    cdpersona: number,
    fechaInicio: string,
    fechaFin: string
  ): Promise<AgendaDisponibilidad[]>;

  /**
   * Busca asesores con disponibilidad
   */
  abstract buscarAsesoresDisponibles(
    fecha: string,
    duracionHoras: number,
    municipio?: string
  ): Promise<number[]>;

  // === Profesionales (PPR) ===

  /**
   * Busca profesionales por DNI y/o nombre.
   * 
   * Legacy: BusquedaActivity.java con TABLE_NAME_TPSC_PROFESIONALES
   * SQL: SELECT NMCONSECUTIVO, DNI, DSNOMBRE, DSMAIL FROM TPSC_PROFESIONALES
   *      WHERE FEBAJA IS NULL AND DNI LIKE '%value%' AND DSNOMBRE LIKE '%value%'
   * 
   * Usado para tipo de cita SEGUIMIENTO_INTEGRAL_CLIENTE (25)
   * 
   * @param dni DNI parcial del profesional (opcional)
   * @param nombre Nombre parcial del profesional (opcional)
   */
  abstract buscarProfesionales(dni?: string, nombre?: string): Promise<Profesional[]>;

  /**
   * Busca profesionales con paginación.
   * Sigue el patrón de BeneficiarioRepository.buscarPaginado()
   * 
   * Legacy: BusquedaActivity.realizarBusqueda() con SuraAsyncDataProvider
   * 
   * Usado para tipo de cita SEGUIMIENTO_INTEGRAL_CLIENTE (25)
   * 
   * @param dni DNI parcial del profesional (opcional)
   * @param nombre Nombre parcial del profesional (opcional)
   * @param page Número de página (0-based), default 0
   * @param size Tamaño de página, default 10
   */
  abstract buscarProfesionalesPaginado(
    dni?: string, 
    nombre?: string, 
    page?: number, 
    size?: number
  ): Promise<AgendaProfesionalPageResponse>;

  // === Configuración ===

  /**
   * Obtiene la configuración de citas del sistema.
   * 
   * Legacy: GeneralParameters, Constants.java
   * - CITA_MINIMA_DURACION (default: 0)
   * - CITA_MAXIMA_DURACION (default: 24)
   * - HOLIDAYS (lista de festivos)
   * 
   * @returns Configuración con límites de duración y fechas festivas
   */
  abstract obtenerConfiguracion(): Promise<ConfiguracionCita>;
}
